import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dropdown, Flex, Icon, M, SidePanel } from 'design-system-web';
import { WorkOrderModuleCategoriesSettings } from 'lane-shared/domains/workOrder';
import { ALL_ISSUE_TYPES_VALUE } from './coverageHoursPanelUtils';
import { useCoverageHoursPanelOptions } from './useCoverageHoursPanelOptions';
import styles from './CreateCoverageHoursPanel.scss';

type CreateCoverageHoursProps = {
  categories: WorkOrderModuleCategoriesSettings[];
  categoryValue: string | null;
  onCategoryChange: (value: string) => void;
  issueTypeValue: string;
  onIssueTypeChange: (value: string) => void;
};

function CreateCoverageHours({
  categories,
  categoryValue,
  onCategoryChange,
  issueTypeValue,
  onIssueTypeChange,
}: CreateCoverageHoursProps) {
  const { t } = useTranslation();
  const { categoryItems, issueTypeItems } = useCoverageHoursPanelOptions(
    categories,
    categoryValue
  );

  const isAllIssueTypesSelected =
    Boolean(categoryValue) && issueTypeValue === ALL_ISSUE_TYPES_VALUE;

  return (
    <Flex direction="column" gap={4} className={styles.panelContent}>
      <Dropdown
        id="coverage-hours-category-select"
        testId="coverage-hours-category-dropdown"
        doTranslation={false}
        isFullWidth
        items={categoryItems}
        value={categoryValue}
        onChange={item => onCategoryChange(String(item.value))}
        label={t(
          'web.admin.serviceRequest.settings.coverageHours.create.category'
        )}
        placeholder={t(
          'web.admin.serviceRequest.settings.coverageHours.create.categoryPlaceholder'
        )}
        fixedLabel
        isRequired
        menuPosition="fixed"
      />

      {categoryValue && (
        <>
          <Dropdown
            id="coverage-hours-issue-type-select"
            testId="coverage-hours-issue-type-dropdown"
            doTranslation={false}
            isFullWidth
            items={issueTypeItems}
            value={issueTypeValue}
            onChange={item => onIssueTypeChange(String(item.value))}
            label={t(
              'web.admin.serviceRequest.settings.coverageHours.create.issueType'
            )}
            fixedLabel
            menuPosition="fixed"
          />

          {isAllIssueTypesSelected && (
            <Flex gap={2} align="flex-start" className={styles.infoMessage}>
              <Icon
                name="info-circle"
                set="FontAwesome"
                type="far"
                className={styles.infoIcon}
              />
              <M className={styles.infoText}>
                {t(
                  'web.admin.serviceRequest.settings.coverageHours.create.allIssueTypesInfo'
                )}
              </M>
            </Flex>
          )}
        </>
      )}
    </Flex>
  );
}

type Props = {
  isOpen: boolean;
  onClose: () => void;
  channelId: string;
  categories: WorkOrderModuleCategoriesSettings[];
};

export function CreateCoverageHoursPanel({
  isOpen,
  onClose,
  channelId: _channelId,
  categories,
}: Props) {
  const { t } = useTranslation();
  const [categoryValue, setCategoryValue] = useState<string | null>(null);
  const [issueTypeValue, setIssueTypeValue] = useState<string>(
    ALL_ISSUE_TYPES_VALUE
  );

  const handleCategoryChange = (value: string) => {
    setCategoryValue(value);
    setIssueTypeValue(ALL_ISSUE_TYPES_VALUE);
  };

  const handleClose = () => {
    setCategoryValue(null);
    setIssueTypeValue(ALL_ISSUE_TYPES_VALUE);
    onClose();
  };

  return (
    <SidePanel
      id="create-coverage-hours-panel"
      isOpen={isOpen}
      onClose={handleClose}
      header={t(
        'web.admin.serviceRequest.settings.coverageHours.create.title'
      )}
      size="small"
      backdropEnabled
      footerActions={
        <>
          <Button variant="primary" size="large" disabled>
            {t(
              'web.admin.serviceRequest.settings.coverageHours.create.save'
            )}
          </Button>
          <Button variant="secondary" size="large" onClick={handleClose}>
            {t(
              'web.admin.serviceRequest.settings.coverageHours.create.cancel'
            )}
          </Button>
        </>
      }
    >
      <CreateCoverageHours
        categories={categories}
        categoryValue={categoryValue}
        onCategoryChange={handleCategoryChange}
        issueTypeValue={issueTypeValue}
        onIssueTypeChange={setIssueTypeValue}
      />
    </SidePanel>
  );
}
